import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  Home,
  Package,
  ShoppingBag,
  Truck,
  Palette,
  Warehouse,
  MessageSquare,
  Gift,
  Heart,
  Star,
  Store,
  Settings,
  ChevronDown,
  ChevronRight,
  Users,
  FileText,
  Send,
  Inbox,
  Menu,
  X,
  CreditCard,
  Sparkles,
  LifeBuoy
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

// Separate Mobile Menu Button component for use in main content area
export function MobileMenuButton({ onOpen }) {
  return (
    <div className="lg:hidden sticky top-0 z-[45] bg-slate-50/95 backdrop-blur-sm border-b border-slate-100">
      <div className="px-4 py-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onOpen}
          className="bg-white shadow-sm border-slate-200 hover:bg-slate-50 flex items-center gap-2"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-4 h-4" />
          <span className="text-sm font-medium">Menu</span>
        </Button>
      </div>
    </div>
  );
}

// Sidebar menu configuration for Crafter
const crafterMenuItems = [
  {
    id: 'home',
    label: 'Dashboard',
    icon: Home,
    section: 'home'
  },
  {
    id: 'profile',
    label: 'Profilo',
    icon: Star,
    children: [
      { id: 'storefront', label: 'Vetrina', section: 'storefront' },
      { id: 'account', label: 'Utente', section: 'account' },
      { id: 'feedback', label: 'Recensioni', section: 'feedback' },
      { id: 'fans', label: 'Ammiratori', section: 'fans' }
    ]
  },
  {
    id: 'catalog',
    label: 'Catalogo',
    icon: Palette,
    children: [
      { id: 'products', label: 'Prodotti', section: 'products' },
      { id: 'inventory', label: 'Magazzino', section: 'inventory' }
    ]
  },
  {
    id: 'sales',
    label: 'Ordini',
    icon: Package,
    children: [
      { id: 'orders', label: 'Da gestire', section: 'orders' },
      { id: 'orders-completed', label: 'Completati', section: 'orders', filter: 'completed' }
    ]
  },
  {
    id: 'customizations',
    label: 'Personalizzazioni',
    icon: Sparkles,
    children: [
      { id: 'client-requests', label: 'Richieste dirette', section: 'client-requests' },
      { id: 'proposals', label: 'Proposte inviate', section: 'proposals' }
    ]
  },
  {
    id: 'assistance',
    label: 'Assistenza ordine',
    icon: LifeBuoy,
    section: 'assistance'
  },
  {
    id: 'shipping-menu',
    label: 'Spedizioni',
    icon: Truck,
    children: [
      { id: 'shipping', label: 'Regole di spedizione', section: 'shipping' }
    ]
  },
  {
    id: 'payments',
    label: 'Pagamenti',
    icon: CreditCard,
    children: [
      { id: 'stripe-setup', label: 'Registrazione', section: 'stripe-setup' },
      { id: 'payouts', label: 'Prelievo incassi', section: 'payouts' }
    ]
  },
  {
    id: 'settings',
    label: 'Impostazioni',
    icon: Settings,
    section: 'settings'
  }
];

// Sidebar menu configuration for Client
const clientMenuItems = [
  {
    id: 'home',
    label: 'Dashboard',
    icon: Home,
    section: 'home'
  },
  {
    id: 'client-orders',
    label: 'I miei ordini',
    icon: ShoppingBag,
    children: [
      { id: 'active-orders', label: 'Ordini attivi', section: 'active-orders' },
      { id: 'completed-orders', label: 'Completati', section: 'completed-orders' }
    ]
  },
  {
    id: 'requests',
    label: 'Le mie richieste',
    icon: Gift,
    children: [
      { id: 'open-requests', label: 'Aperte', section: 'open-requests' },
      { id: 'inprogress-requests', label: 'In creazione', section: 'inprogress-requests' }
    ]
  },
  /*{
    id: 'messages',
    label: 'Messaggi',
    icon: MessageSquare,
    section: 'messages'
  },*/
  {
    id: 'assistance',
    label: 'Assistenza ordine',
    icon: LifeBuoy,
    section: 'assistance'
  },
  {
    id: 'favorites',
    label: 'Preferiti',
    icon: Heart,
    section: 'favorites'
  },
  {
    id: 'settings',
    label: 'Impostazioni',
    icon: Settings,
    section: 'settings'
  }
];

function MenuItem({ item, activeSection, activeFilter, onSectionChange, expandedMenus, toggleExpanded, badges }) {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedMenus.includes(item.id);
  const isActive = item.section === activeSection || 
    (hasChildren && item.children.some(child => child.section === activeSection));
  
  const Icon = item.icon;
  // Il badge del gruppo è la somma dei badge dei figli (evita di mostrare
  // conteggi di un altro ruolo quando due voci condividono lo stesso id)
  const badgeCount = hasChildren
    ? item.children.reduce((sum, child) => sum + (badges?.[child.id] || 0), 0)
    : (badges?.[item.id] || 0);

  return (
    <div>
      <button
        onClick={() => {
          if (hasChildren) {
            toggleExpanded(item.id);
          } else if (item.section) {
            onSectionChange(item.section);
          }
        }}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
          isActive 
            ? "bg-[#008080]/10 text-[#008080]" 
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        )}
        data-testid={`sidebar-${item.id}`}
      >
        <div className="flex items-center gap-3">
          <Icon className={cn("w-5 h-5", isActive ? "text-[#008080]" : "text-slate-400")} />
          <span>{item.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {badgeCount > 0 && (
            <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
              {badgeCount}
            </Badge>
          )}
          {hasChildren && (
            <div
              className={cn(
                "transition-transform duration-200",
                isExpanded ? "rotate-180" : "rotate-0"
              )}
            >
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>
          )}
        </div>
      </button>

      {/* Submenu with CSS transition instead of AnimatePresence */}
      {hasChildren && (
        <div 
          className={cn(
            "overflow-hidden transition-all duration-200 ease-in-out",
            isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200 pl-4">
            {item.children.map((child) => {
              const childBadge = badges?.[child.id] || 0;
              // Determine if this child is active based on section and filter
              const isChildActive = activeSection === child.section && 
                ((child.filter && activeFilter === child.filter) || (!child.filter && !activeFilter));
              return (
                <button
                  key={child.id}
                  onClick={() => onSectionChange(child.section, child.filter || null)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-200",
                    isChildActive
                      ? "bg-[#008080] text-white"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  )}
                  data-testid={`sidebar-${child.id}`}
                >
                  <span>{child.label}</span>
                  {childBadge > 0 && (
                    <Badge className={cn(
                      "text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center",
                      isChildActive
                        ? "bg-white text-[#008080]"
                        : "bg-red-500 text-white"
                    )}>
                      {childBadge}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardSidebar({ 
  role, // 'client' or 'crafter'
  activeSection, 
  activeFilter = null, // 'completed' | 'active' | null - for orders tab filtering
  onSectionChange, 
  onRoleSwitch, 
  isCrafterEnabled,
  badges = {},
  userName,
  mobileMenuOpen = false,
  onMobileMenuOpenChange
}) {
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState([]); // Start with all menus closed
  
  // Use internal state if no external control provided
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  const isMenuOpen = onMobileMenuOpenChange ? mobileMenuOpen : internalMobileMenuOpen;
  const setMenuOpen = onMobileMenuOpenChange || setInternalMobileMenuOpen;

  const menuItems = role === 'crafter' ? crafterMenuItems : clientMenuItems;

  const toggleExpanded = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleSectionChange = (section, filter = null) => {
    // All sections now stay within the dashboard
    onSectionChange(section, filter);
    setMenuOpen(false);
  };

  const SidebarContent = () => (
    <>
      {/* Header with Role Switch */}
      <div className="p-4 border-b border-slate-200">
        <h2 className="text-lg font-bold text-slate-800 mb-4">La Mia Area</h2>
        
        {/* Role Switch */}
        <div className="flex bg-slate-100 rounded-xl p-1" data-testid="role-switch">
          <button
            onClick={() => onRoleSwitch('client')}
            className={cn(
              "flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-300",
              role === 'client'
                ? "bg-[#00CED1] text-white shadow-md"
                : "text-slate-600 hover:text-slate-800"
            )}
            data-testid="switch-cliente"
          >
            Cliente
          </button>
          <button
            onClick={() => onRoleSwitch('crafter')}
            className={cn(
              "flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-300",
              role === 'crafter'
                ? "bg-[#DAA520] text-white shadow-md"
                : "text-slate-600 hover:text-slate-800"
            )}
            data-testid="switch-artigiano"
          >
            {isCrafterEnabled ? 'Artigiano' : 'Diventa Artigiano'}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            activeSection={activeSection}
            activeFilter={activeFilter}
            onSectionChange={handleSectionChange}
            expandedMenus={expandedMenus}
            toggleExpanded={toggleExpanded}
            badges={badges}
          />
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200">
        <p className="text-xs text-slate-400 text-center">
          {userName && <span className="block mb-1 text-slate-500 font-medium">{userName}</span>}
          Rarifex © 2026
        </p>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isMenuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-200"
            onClick={() => setMenuOpen(false)}
          />
          <aside
            className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] bg-white z-50 flex flex-col shadow-2xl transition-transform duration-200"
          >
            <div className="flex justify-end p-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMenuOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside 
        className="hidden lg:flex w-[280px] bg-white border-r border-slate-200 flex-col sticky top-0 h-screen flex-shrink-0"
        data-testid="dashboard-sidebar"
      >
        <SidebarContent />
      </aside>
    </>
  );
}
